import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./Reveal";
import { TEAM_AREAS, IMG } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", area_of_interest: "", background: "", message: "" };

const inputCls =
  "w-full bg-abyss/70 border border-polar/20 text-ice placeholder:text-tech/70 px-4 py-3 text-sm focus:outline-none focus:border-polar transition-colors duration-300";

export default function Team() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Name is required";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid email is required";
    if (!form.area_of_interest) errs.area_of_interest = "Select an area of interest";
    if (form.background.trim().length < 2) errs.background = "A short background note is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries/interest`, { ...form, message: form.message || null });
      toast.success("Expression of interest received. Thank you for reaching out.");
      setForm(EMPTY);
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="team" data-testid="team-section" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMG.glacier} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-15" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/70 to-midnight" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <SectionTag index="08" label="Mission Team & Recruitment" />
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice leading-[0.95] max-w-4xl">
            Some expeditions <span className="text-polar">change everything</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-ice/70 leading-relaxed">
            We are exploring the expertise needed to connect satellite intelligence, geospatial AI and field
            observations in one of Earth's most remote environments.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          <div className="lg:col-span-5">
            <div className="space-y-3">
              {TEAM_AREAS.map((a, i) => (
                <Reveal key={a} delay={0.05 * i}>
                  <div className="flex items-center gap-4 border-b border-polar/10 pb-3" data-testid={`team-area-${i}`}>
                    <span className="font-mono text-[10px] text-mission tracking-[0.3em] w-8">0{i + 1}</span>
                    <span className="font-display font-semibold text-lg sm:text-xl text-ice tracking-wide">{a}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-8 text-xs font-mono text-tech leading-relaxed tracking-wide" data-testid="team-disclaimer">
                NOTE — This is an expression of interest only. No confirmed vacancies, compensation, expedition berths
                or funded travel are being advertised at this stage.
              </p>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={submit} noValidate className="border border-polar/20 bg-[#0B1F35]/85 backdrop-blur-md p-7 sm:p-9" data-testid="team-eoi-form">
              <div className="font-mono text-[10px] tracking-[0.3em] text-polar mb-7">EXPRESSION OF INTEREST</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="eoi-name" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">NAME *</label>
                  <input id="eoi-name" data-testid="team-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
                  {errors.name && <p className="text-mission text-xs mt-1.5" data-testid="team-error-name">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="eoi-email" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">EMAIL *</label>
                  <input id="eoi-email" type="email" data-testid="team-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.org" />
                  {errors.email && <p className="text-mission text-xs mt-1.5" data-testid="team-error-email">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="eoi-area" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">AREA OF INTEREST *</label>
                <select id="eoi-area" data-testid="team-input-role" className={`${inputCls} appearance-none`} value={form.area_of_interest} onChange={set("area_of_interest")}>
                  <option value="" disabled>Select an area</option>
                  {TEAM_AREAS.map((a) => (
                    <option key={a} value={a} className="bg-abyss">{a}</option>
                  ))}
                  <option value="Other" className="bg-abyss">Other</option>
                </select>
                {errors.area_of_interest && <p className="text-mission text-xs mt-1.5" data-testid="team-error-area">{errors.area_of_interest}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="eoi-background" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">BACKGROUND *</label>
                <input id="eoi-background" data-testid="team-input-background" className={inputCls} value={form.background} onChange={set("background")} placeholder="Your field, affiliation and relevant experience" />
                {errors.background && <p className="text-mission text-xs mt-1.5" data-testid="team-error-background">{errors.background}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="eoi-message" className="block font-mono text-[10px] tracking-[0.2em] text-tech mb-2">MESSAGE (OPTIONAL)</label>
                <textarea id="eoi-message" rows={3} data-testid="team-input-message" className={`${inputCls} resize-y`} value={form.message} onChange={set("message")} placeholder="Anything else you'd like to share" />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="team-submit-button"
                className="mt-8 w-full bg-polar text-midnight font-mono text-xs uppercase tracking-[0.25em] py-4 clip-corner hover:bg-ice transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "Transmitting…" : "Express Interest in the Mission"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
